/**
 * New Estimate Page - T181
 *
 * Create a new cost estimate
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, Input, Select } from '../components/ui';
import { useCreateEstimate } from '../hooks/useEstimates';
import { useCustomerList, type Customer } from '../hooks/useCustomers';
import { useToastHelpers } from '../components/ui';

export const Route = createFileRoute('/estimates_/new')({
  component: NewEstimatePage,
});

interface EstimateItemInput {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
}

function NewEstimatePage() {
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const createEstimate = useCreateEstimate();
  const { data: customersData } = useCustomerList({ limit: 100 });
  const customers = customersData?.customers || [];

  // Form state
  const [customerId, setCustomerId] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState('');
  const [subject, setSubject] = useState('');
  const [customerReference, setCustomerReference] = useState('');
  const [introText, setIntroText] = useState('');
  const [closingText, setClosingText] = useState('');
  const [vatRate, setVatRate] = useState(19);
  const [items, setItems] = useState<EstimateItemInput[]>([
    { id: crypto.randomUUID(), description: '', quantity: 1, unit: 'Stück', unitPrice: 0 },
  ]);

  const addItem = () => {
    setItems([
      ...items,
      { id: crypto.randomUUID(), description: '', quantity: 1, unit: 'Stück', unitPrice: 0 },
    ]);
  };

  const removeItem = (id: string) => {
    if (items.length <= 1) return;
    setItems(items.filter((item) => item.id !== id));
  };

  const updateItem = (id: string, field: keyof EstimateItemInput, value: string | number) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!customerId) {
      toast.error('Bitte wählen Sie einen Kunden');
      return;
    }

    if (items.some((item) => !item.description)) {
      toast.error('Bitte füllen Sie alle Positionen aus');
      return;
    }

    try {
      const estimate = await createEstimate.mutateAsync({
        customerId,
        issueDate,
        validUntil: validUntil || undefined,
        subject: subject || undefined,
        customerReference: customerReference || undefined,
        introText: introText || undefined,
        closingText: closingText || undefined,
        vatRate,
        items: items.map((item, index) => ({
          position: index + 1,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
        })),
      });

      toast.success('Angebot erstellt');
      navigate({ to: '/estimates/$id', params: { id: estimate.id } });
    } catch {
      toast.error('Fehler beim Erstellen des Angebots');
    }
  };

  // Calculate totals
  const netTotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const vatAmount = netTotal * (vatRate / 100);
  const grossTotal = netTotal + vatAmount;

  return (
    <ProtectedRoute>
      <AppShell>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-white">Neues Angebot</h1>
              <p className="text-white/50 mt-1">Kostenvoranschlag erstellen</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => navigate({ to: '/estimates' })}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={createEstimate.isPending}>
                {createEstimate.isPending ? 'Erstelle...' : 'Erstellen'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-4">Grunddaten</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Kunde *</label>
                    <Select
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      options={[
                        { value: '', label: 'Kunde auswählen...' },
                        ...customers.map((c: Customer) => ({
                          value: c.id,
                          label: `${c.companyName} (${c.customerNumber})`,
                        })),
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Betreff</label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="z.B. Angebot für Webentwicklung"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Kundenreferenz</label>
                    <Input
                      value={customerReference}
                      onChange={(e) => setCustomerReference(e.target.value)}
                      placeholder="z.B. Anfrage vom 01.01.2026"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Einleitungstext</label>
                    <textarea
                      value={introText}
                      onChange={(e) => setIntroText(e.target.value)}
                      placeholder="Vielen Dank für Ihre Anfrage..."
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:border-white/20 focus:outline-none resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              </GlassPanel>

              {/* Items */}
              <GlassPanel>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-white">Positionen</h2>
                  <Button type="button" variant="secondary" size="sm" onClick={addItem}>
                    + Position
                  </Button>
                </div>

                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={item.id} className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-sm text-white/40">Position {index + 1}</span>
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Entfernen
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div className="sm:col-span-2">
                          <Input
                            value={item.description}
                            onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                            placeholder="Beschreibung"
                            required
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                            placeholder="Menge"
                            min="0"
                            step="0.001"
                          />
                          <Input
                            value={item.unit}
                            onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                            placeholder="Einheit"
                          />
                          <Input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                            placeholder="Preis"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="mt-6 pt-4 border-t border-white/10">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Netto</span>
                    <span className="text-white">{netTotal.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-white/50">MwSt. ({vatRate}%)</span>
                    <span className="text-white">{vatAmount.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold mt-2">
                    <span className="text-white">Gesamt</span>
                    <span className="text-white">{grossTotal.toFixed(2)} €</span>
                  </div>
                </div>
              </GlassPanel>

              {/* Closing Text */}
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-4">Schlusstext</h2>
                <textarea
                  value={closingText}
                  onChange={(e) => setClosingText(e.target.value)}
                  placeholder="Wir freuen uns auf Ihre Rückmeldung..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:border-white/20 focus:outline-none resize-none"
                  rows={3}
                />
              </GlassPanel>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Dates */}
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-4">Datum</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Erstelldatum *</label>
                    <Input
                      type="date"
                      value={issueDate}
                      onChange={(e) => setIssueDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Gültig bis</label>
                    <Input
                      type="date"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                    />
                    <p className="text-xs text-white/40 mt-1">Leer = unbefristet</p>
                  </div>
                </div>
              </GlassPanel>

              {/* VAT */}
              <GlassPanel>
                <h2 className="text-lg font-medium text-white mb-4">Mehrwertsteuer</h2>
                <Select
                  value={String(vatRate)}
                  onChange={(e) => setVatRate(parseInt(e.target.value))}
                  options={[
                    { value: '19', label: '19%' },
                    { value: '7', label: '7%' },
                    { value: '0', label: '0%' },
                  ]}
                />
              </GlassPanel>
            </div>
          </div>
        </form>
      </AppShell>
    </ProtectedRoute>
  );
}
